import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllDishOfTheDays,
  getSingleDishOfTheDay,
  createDishOfTheDay,
  updateDishOfTheDay,
  deleteDishOfTheDay,
  filterByDay,
} from "@/api/new-api/staff-management//menu-manage/special-menu/dish-of-day";

// Thunks

export const fetchAllDishOfTheDays = createAsyncThunk(
  "dishOfTheDay/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllDishOfTheDays(restaurant_id);
    if (res.status === "success") return res.data.dishes;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleDishOfTheDay = createAsyncThunk(
  "dishOfTheDay/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleDishOfTheDay(id);
    if (res.status === "success") return res.data.dish;
    return rejectWithValue(res.message);
  }
);

export const createNewDishOfTheDay = createAsyncThunk(
  "dishOfTheDay/create",
  async (data, { rejectWithValue }) => {
    const res = await createDishOfTheDay(data);
    if (res.status === "success") return res.data.dish;
    return rejectWithValue(res.message);
  }
);

export const updateExistingDishOfTheDay = createAsyncThunk(
  "dishOfTheDay/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateDishOfTheDay(id, data);
    if (res.status === "success") return res.data.dish;
    return rejectWithValue(res.message);
  }
);

export const deleteDishOfTheDayById = createAsyncThunk(
  "dishOfTheDay/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteDishOfTheDay(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

export const filterDishOfTheDayByDay = createAsyncThunk(
  "dishOfTheDay/filterByDay",
  async ({ restaurant_id, day_id }, { rejectWithValue }) => {
    const res = await filterByDay(restaurant_id, day_id);
    if (res.status === "success") return res.data.dish_of_the_days;
    return rejectWithValue(res.message);
  }
);

// Initial State
const initialState = {
  dishOfTheDays: [],
  singleDishOfTheDay: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  filterLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice
const dishOfTheDaySlice = createSlice({
  name: "dishOfTheDay",
  initialState,
  reducers: {
    clearDishOfTheDayMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllDishOfTheDays.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllDishOfTheDays.fulfilled, (state, action) => {
        state.getLoading = false;
        state.dishOfTheDays = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllDishOfTheDays.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleDishOfTheDay.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleDishOfTheDay.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleDishOfTheDay = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleDishOfTheDay.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewDishOfTheDay.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewDishOfTheDay.fulfilled, (state, action) => {
        state.createLoading = false;
        state.dishOfTheDays.push(action.payload);
        state.success = "Dish of the Day created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewDishOfTheDay.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingDishOfTheDay.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingDishOfTheDay.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.dishOfTheDays = state.dishOfTheDays.map((dish) =>
          dish.id === action.payload.id ? action.payload : dish
        );
        state.success = "Dish of the Day updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingDishOfTheDay.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteDishOfTheDayById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteDishOfTheDayById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.dishOfTheDays = state.dishOfTheDays.filter(
          (dish) => dish.id !== action.payload
        );
        state.success = "Dish of the Day deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteDishOfTheDayById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Filter By Day
      .addCase(filterDishOfTheDayByDay.pending, (state) => {
        state.filterLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(filterDishOfTheDayByDay.fulfilled, (state, action) => {
        state.filterLoading = false;
        state.dishOfTheDays = action.payload;
        state.requestStatus = true;
      })
      .addCase(filterDishOfTheDayByDay.rejected, (state, action) => {
        state.filterLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearDishOfTheDayMessages } = dishOfTheDaySlice.actions;
export default dishOfTheDaySlice.reducer;
